import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useOrganization } from '@/hooks/useOrganizations';
import { Group } from '@visx/group';
import { Pie } from '@visx/shape';
import { Text } from '@visx/text';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import prettyBytes from 'pretty-bytes';
import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface StorageProject {
  id: string;
  name: string;
  storageUsedBytes: number;
}

interface StorageChartProps {
  projects: StorageProject[];
}

const COLORS = [
  '#6366f1', // indigo-500
  '#8b5cf6', // violet-500
  '#a855f7', // purple-500
  '#ec4899', // pink-500
  '#f43f5e', // rose-500
  '#f97316', // orange-500
  '#eab308', // yellow-500
  '#22c55e', // green-500
  '#14b8a6', // teal-500
  '#06b6d4', // cyan-500
  '#3b82f6', // blue-500
  '#64748b', // slate-500
];

const StorageChart: FunctionComponent<StorageChartProps> = ({ projects }) => {
  const { t } = useTranslation('common');
  const organization = useOrganization();
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<{
    name: string;
    bytes: number;
    percentage: number;
  }>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ detectBounds: true, scroll: true });

  const storageUsed = organization?.storageUsedBytes || 0;
  const storageLimit = organization?.storageLimitBytes || 1;
  const ephemeralBytes = (organization as any)?.ephemeralBytes || 0;
  const usagePercent = (storageUsed / storageLimit) * 100;

  const chartData = useMemo(() => {
    const sorted = [...projects].sort((a, b) => b.storageUsedBytes - a.storageUsedBytes);
    const data = sorted
      .filter((p) => p.storageUsedBytes > 0)
      .map((p, i) => ({
        label: p.name,
        value: p.storageUsedBytes,
        color: COLORS[i % COLORS.length],
      }));

    return data;
  }, [projects]);

  const width = 160;
  const height = 160;
  const radius = Math.min(width, height) / 2;
  const innerRadius = radius - 36;

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-sm'>{t('dashboard.storage.title')}</CardTitle>
        <CardDescription>{t('dashboard.storage.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className='flex items-start gap-6'>
          <div ref={containerRef} className='relative flex-shrink-0'>
            <svg width={width} height={height}>
              <Group top={height / 2} left={width / 2}>
                <Pie
                  data={chartData}
                  pieValue={(d) => d.value}
                  outerRadius={radius}
                  innerRadius={innerRadius}
                  cornerRadius={3}
                  padAngle={0.02}>
                  {(pie) =>
                    pie.arcs.map((arc, i) => {
                      const [cx, cy] = pie.path.centroid(arc);
                      return (
                        <g
                          key={`arc-${i}`}
                          onMouseMove={(e) => {
                            const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                            showTooltip({
                              tooltipData: {
                                name: arc.data.label,
                                bytes: arc.data.value,
                                percentage: Math.round((arc.data.value / storageLimit) * 1000) / 10,
                              },
                              tooltipLeft: e.clientX - rect.left,
                              tooltipTop: e.clientY - rect.top,
                            });
                          }}
                          onMouseLeave={hideTooltip}>
                          <path
                            d={pie.path(arc) || ''}
                            fill={arc.data.color}
                            className='transition-opacity hover:opacity-80 cursor-pointer'
                          />
                        </g>
                      );
                    })
                  }
                </Pie>
                <Text
                  textAnchor='middle'
                  verticalAnchor='middle'
                  dy={-8}
                  className='fill-slate-900 font-medium dark:fill-white text-lg font-bold'>
                  {`${(Math.round(usagePercent * 100) / 100).toLocaleString()}%`}
                </Text>
                <Text
                  textAnchor='middle'
                  verticalAnchor='middle'
                  dy={12}
                  className='fill-slate-500 font-medium dark:fill-stone-400 text-[11px] font-medium'>
                  {t('dashboard.storage.used')}
                </Text>
              </Group>
            </svg>
            {tooltipOpen && tooltipData && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                className='!bg-slate-800 !text-white !rounded-md !px-3 !py-2 !text-xs !shadow-lg'>
                <div className='font-semibold'>{tooltipData.name}</div>
                <div className='text-slate-300'>
                  {prettyBytes(tooltipData.bytes)} ({tooltipData.percentage}%)
                </div>
              </TooltipInPortal>
            )}
          </div>
          <div className='flex-1 min-w-0 gap-1.5 flex flex-col justify-start'>
            {projects
              .filter((p) => p.storageUsedBytes > 0)
              .sort((a, b) => b.storageUsedBytes - a.storageUsedBytes)
              .slice(0, 5)
              .map((p, i) => {
                const pct = Math.round((p.storageUsedBytes / storageLimit) * 100);

                return (
                  <div key={p.id} className='flex items-center gap-2 text-xs whitespace-nowrap'>
                    <div
                      className='size-2.5 rounded-full flex-shrink-0'
                      style={{ background: COLORS[i % COLORS.length] }}
                    />
                    <span className='flex-1 text-slate-600 dark:text-stone-300 font-medium truncate'>{p.name}</span>
                    <span className='font-mono font-medium text-slate-500 dark:text-stone-400 flex-shrink-0 w-14 text-right flex-shrink-0 overflow-hidden'>
                      {prettyBytes(p.storageUsedBytes)}
                    </span>
                    <span className='font-mono font-medium text-slate-400 dark:text-stone-500 flex-shrink-0 w-6 flex-shrink-0 text-right overflow-hidden'>
                      {pct}%
                    </span>
                  </div>
                );
              })}
            {ephemeralBytes > 0 && (
              <div className='text-[10px] text-slate-500 dark:text-stone-400 italic pt-1 mt-1 border-t border-slate-100 dark:border-stone-800'>
                {t('dashboard.storage.ephemeralNote', { size: prettyBytes(ephemeralBytes) })}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StorageChart;
